import {DomainKeys, Peer, State} from '../../state/types/state.js';
import {HandleReturnType} from '../../domain.js';
import {Activity, activityDomain, ActivityId, ActivityPeer} from '../../state/types/activity.js';
import * as gatewayRequest from './gatewayRequest.js';
import * as activityState from './state.js';
import {
    byId,
    byIdInDomain,
    byIdInDomainThrows,
    byIdThrows,
    isPeerVisible,
    setPeer,
    visiblePeers
} from '../../state/peers.js';
import {peerCreated, peerFactoriesAdded, peerFactoriesRemoved, peerRequested} from './messages.js';
import {MessageType, SendMessageAction} from '../../node.js';
import {Draft, produce} from 'immer';
import {Factory, FactoryId, PeerType} from '../../state/types/factory.js';
import {Reason, throwReason} from '../../reason.js';
import {
    activityDomainUri,
    activityFactoryAlreadyRegistered,
    activityInvalidPeer,
    activityMissingFactory
} from './constants.js';
import {PeerMessage, RequestMessage} from '../../common/types/messages.js';
import {error, success} from '../../common/messages.js';
import {AddPeerFactoriesRequest, CreatePeerRequest, RemovePeerFactoriesRequest} from './types/requests.js';
import {PeerId} from '../../state/types/common.js';
import {requestId} from '../../common/ids.js';
import {forRequest} from '../../common/tokens.js';
import {setGatewayRequest} from '../../state/core.js';
import {contextById} from '../../common/context/state.js';
import {GatewayRequest, GwRequestId} from '../../state/types/pending-requests.js';
import {Context, ContextData, ContextId} from '../../state/types/context.js';
import {some, WithRequired} from '../../common/utilities.js';

type JoinedPeer = WithRequired<Peer, DomainKeys.Activity>;

function addFactories(state: State, peer: JoinedPeer, factories: Factory[]) {
    const factoriesByType: { [type: PeerType]: Factory[] } = factories.reduce((grouped, f) => {
        if (grouped[f.peer_type]) {
            grouped[f.peer_type].push(f);
        } else {
            grouped[f.peer_type] = [f];
        }
        return grouped;
    }, {} as { [type: PeerType]: Factory[] });
    state = setPeer(state, peer.id, produce(peer, (draft: Draft<JoinedPeer>) => {
        draft[activityDomain].factories = draft[activityDomain].factories || {}
        for (const [ft, fs] of Object.entries(factoriesByType)) {
            draft[activityDomain].factories[ft] = fs[0];
        }

    }));
    state = activityState.addFactories(state, Object.keys(factoriesByType), peer.id);
    return state;
}

function broadcastFactoriesAdded(state: State, owner: JoinedPeer, factories: Factory[]) {
    return visiblePeers(state, activityDomain, owner).map((peer) => {
        return peerFactoriesAdded(peer.source, peer.id, owner.id, factories);
    });
}

export function announceExisting(state: State, peer: JoinedPeer): SendMessageAction<'peer-factories-added'>[] {
    return Array.from(new Set(Object.values<readonly PeerId[]>(activityState.factories(state) || {}).flat(1)))
        .map((peerId) => {
            return byIdInDomain(state, peerId, DomainKeys.Activity);
        })
        .filter((p): p is JoinedPeer => {
            return !!p;
        })
        .filter(p => {
            return isPeerVisible(DomainKeys.Activity, peer, p);
        })
        .map((factoryPeer) => {
            const factories = factoryPeer[DomainKeys.Activity].factories;
            if (factories) {
                return peerFactoriesAdded(peer.source, peer.id, factoryPeer.id, Object.values<Factory>(factories));
            }
        })
        .filter(some<SendMessageAction<'peer-factories-added'>>);
}


function validateFactory(peer, factory: Factory) {
    const peerType = factory.peer_type;
    if (peer[activityDomain]?.factories?.[peerType]) {
        throwReason(activityFactoryAlreadyRegistered, `Factory for type ${peerType} is already registered by this peer`);
    }
}

export function add(state: State, source, request: AddPeerFactoriesRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, factories} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    factories.forEach(f => validateFactory(peer, f));

    const updatedState = addFactories(state, peer, factories);
    const messages: Array<SendMessageAction<MessageType>> = broadcastFactoriesAdded(updatedState, peer, factories);
    return [updatedState, messages.concat(success(activityDomainUri, source, requestId, peerId))]
}


function broadcastRemoved(state: State, owner: JoinedPeer, factoryIds, includeOwner: boolean): SendMessageAction<'peer-factories-removed'>[] {
    const ownerId = owner.id;
    return visiblePeers(state, activityDomain, owner)
        .filter((p: Peer) => {
            return includeOwner || (ownerId !== p.id);
        }).map((peer) => {
            return peerFactoriesRemoved(peer.source, peer.id, owner.id, factoryIds);
        });
}

function factoryById(factories: Record<PeerType, Factory> | undefined, id: FactoryId): Factory | undefined {
    return Object.values(factories || {}).find((f) => f.id === id);
}

/**
 * Removes a set of factory ids from a peer. Returns the updated peer and a vector of removed factories
 */
function removeFactoriesFromPeer(peer: Peer, factoryIds: FactoryId[]): [Peer, Factory[]] {
    const factories = peer[activityDomain]?.factories;
    const [updatedPeer, removed] = factoryIds.reduce(([p, removed], factoryId) => {
        const factory = factoryById(factories, factoryId);
        if (factory) {
            return [produce(p, (draft: Draft<Peer>) => {
                if (draft[activityDomain]?.factories) {
                    delete draft[activityDomain]?.factories?.[factory.peer_type];
                    if (Object.keys(draft[activityDomain]?.factories).length === 0) {
                        delete draft[activityDomain]?.factories;
                    }
                }
            }), removed.concat(factory)];
        }
        return [p, removed];
    }, [peer, [] as Factory[]]);
    return [updatedPeer, removed];

}

export function removeFactories(state: State, source, request: RemovePeerFactoriesRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, factory_ids: factoryIds} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const [updatedPeer, removed] = removeFactoriesFromPeer(peer, factoryIds);
    const updatedState = activityState.removeFactories(setPeer(state, peerId, updatedPeer), removed.map(f => f.peer_type), peerId);
    let messages: SendMessageAction<MessageType>[] = broadcastRemoved(updatedState, peer, removed.map(r => r.id), true);
    messages = messages.concat([success(activityDomainUri, source, requestId, peerId)]);
    return [updatedState, messages];
}

export function requestPeer(state: State, owner: Peer, factory: Factory, configuration, token: string, requestId: GwRequestId, activity, context: Context | undefined, peerName): SendMessageAction<MessageType> {
    const activityRequest: { activity?: { id: ActivityId, type: string, context_id?: ContextId, 'initial-context'?: ContextData }, peer_name? } = {};
    if (activity) {
        activityRequest.activity = {
            id: activity.id,
            type: activity.type,
            context_id: context?.id,
            'initial-context': context?.data
        };
    }
    if (peerName) {
        activityRequest.peer_name = peerName;
    }
    // for those wondering why there is no peer type here - the factory id : peer type is 1 : 1
    return peerRequested(owner.source, requestId, owner.id, factory.id, token, {...factory.configuration, ...configuration}, activityRequest);
}

export function peerReady(state: State, peer?: Peer): HandleReturnType {
    const clientRequest = peer?.creationRequest?.clientRequest;
    if (!clientRequest) {
        throwReason(activityInvalidPeer, `Unable to find originating request for a ready message from peer ${peer?.id}`);
    }
    const callerId = clientRequest.peer_id;
    const caller = byId(state, callerId)!;
    return [state, [peerCreated(caller.source, clientRequest.request_id, callerId, peer.id)]];
}

/**
 * Peer is leaving the domain.
 *
 * Updates the state and generates messages for the peer and its registered methods being removed
 */
export function removePeer(state: State, peer: JoinedPeer): HandleReturnType {
    const factories = Object.values<Factory>(peer[activityDomain]?.factories || {});
    if (factories.length > 0) {
        // remove the factories that this peer had
        return [activityState.removeFactories(state, factories.map(f => f.peer_type), peer.id), broadcastRemoved(state, peer, factories.map(f => f.id), false)];
    }
    return [state, []];
}

function createPeerFailed(state: State, request: PeerMessage & RequestMessage | undefined, failure: Reason): HandleReturnType {
    const creator = byId(state, request?.peer_id);
    if (creator) {
        return [state, [error(activityDomainUri, creator.source, request!.request_id, creator.id, failure)]];
    }
    return [state, []];
}

export function handleCreatePeerError(state: State, failure: Reason, incomingR: PeerMessage & RequestMessage | undefined, gatewayR: GatewayRequest): HandleReturnType {
    return createPeerFailed(state, incomingR, failure);
}

function factoryOwner(state: State, ownerId: PeerId | undefined, peerType: PeerType): [Peer, Factory] {
    const owner = byId(state, ownerId ?? activityState.factoriesByType(state, peerType)?.[0]);
    const factory: Factory | undefined = owner?.[activityDomain]?.factories?.[peerType];
    if (!factory) {
        throwReason(activityMissingFactory, `Unable to find factory owner for type ${peerType}`);
    }
    return [owner!, factory!];
}

export function createActivityPeer(state: State, creatorId: PeerId, activityPeer: ActivityPeer, activity: Activity, context: Context, isActivityOwner: boolean, configuration): { state: State, messages: Array<SendMessageAction<MessageType>>, requestId: GwRequestId } {
    const [owner, factory] = factoryOwner(state, undefined, activityPeer.type);
    const creator: Peer = byIdThrows(state, creatorId);
    const [newIds, gwRequestId] = requestId(state.ids);
    const gwRequest = gatewayRequest.activity(gwRequestId, activityPeer, activity, isActivityOwner);
    const token = forRequest(state, creator.identity, gwRequest);
    state = setGatewayRequest({...state, ids: newIds}, gwRequestId, gwRequest);
    return {
        state: state,
        messages: [requestPeer(state, owner, factory, configuration, token, gwRequestId, activity, context, null)],
        requestId: gwRequestId
    }
}

function createPeer(state: State, creatorId: PeerId, factoryOwnerId: PeerId | undefined, peerType: PeerType, peerName, activity, configuration, clientRequest): { state: State, messages: Array<SendMessageAction<MessageType>>, requestId: GwRequestId } {
    const [owner, factory] = factoryOwner(state, factoryOwnerId, peerType);
    const creator = byIdThrows(state, creatorId);
    const [newIds, gwRequestId] = requestId(state.ids);
    const gwRequest = gatewayRequest.factory(gwRequestId, peerType, peerName, {
        request_id: clientRequest.request_id,
        peer_id: clientRequest.peer_id
    });
    const token = forRequest(state, creator.identity, gwRequest);
    state = setGatewayRequest({...state, ids: newIds}, gwRequestId, gwRequest);
    return {
        state,
        messages: [requestPeer(state, owner, factory, configuration, token, gwRequestId, activity, contextById(state, activity?.contextId), peerName)],
        requestId: gwRequestId
    };
}

export function create(state: State, source, request: CreatePeerRequest): HandleReturnType {
    const {
        request_id: requestId,
        peer_id: peerId,
        owner_id: ownerId,
        peer_type: peerType,
        peer_name: peerName,
        activity_id: activityId
    } = request;
    byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const activity = activityState.activityMaybeThrows(state, activityId);
    const result = createPeer(state, peerId, ownerId, peerType, peerName ?? peerType, activity, request.configuration, request);
    return [result.state, result.messages.concat(success(activityDomainUri, source, requestId, peerId))];
}
